if (!customElements.get('wls-double-range-input')) {
  class WLSDoubleRangeInput extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        fromSlider: 'js-from-slider',
        toSlider: 'js-to-slider',
        fromPlaceholder: 'js-from-placeholder',
        toPlaceholder: 'js-to-placeholder',
        pointerEventsNone: 'pointer-events-none',
      };

      this.selectors = {
        fromSlider: this.querySelector(`.${this.classes.fromSlider}`),
        toSlider: this.querySelector(`.${this.classes.toSlider}`),
        fromPlaceholder: this.querySelector(`.${this.classes.fromPlaceholder}`),
        toPlaceholder: this.querySelector(`.${this.classes.toPlaceholder}`),
      };
    }

    /**
     * Lifecycle method called when the element is added to the DOM.
     * Sets up event listeners and initializes the slider.
     * @returns {void}
     */
    connectedCallback() {
      document.addEventListener('update-double-range-input', this.dispatchCustomEvent.bind(this));
    }

    dispatchCustomEvent() {
      this.addEventListeners();
      this.initializeSlider();
    }

    /**
     * Initializes the slider by filling it and setting accessibility.
     * @returns {void}
     */
    initializeSlider() {
      this.fillSlider();
    }

    /**
     * Adds event listeners for slider and input changes.
     * @returns {void}
     */
    addEventListeners() {
      this.selectors.fromSlider.oninput = () => this.controlFromSlider();
      this.selectors.toSlider.oninput = () => this.controlToSlider();
    }

    /**
     * Controls the behavior when the "from" slider is adjusted.
     * @returns {void}
     */
    controlFromSlider() {
      const [from, to] = this.getParsed(this.selectors.fromSlider, this.selectors.toSlider);
      this.fillSlider();
      if (from > to) {
        this.selectors.fromSlider.value = to;
        this.selectors.fromPlaceholder.textContent = to;
        this.dataset.min = to;
      } else {
        this.selectors.fromPlaceholder.textContent = from;
        this.dataset.min = from;
      }
      this.toggleSliderVisibility(from, to);
    }

    /**
     * Controls the behavior when the "to" slider is adjusted.
     * @returns {void}
     */
    controlToSlider() {
      const [from, to] = this.getParsed(this.selectors.fromSlider, this.selectors.toSlider);
      this.fillSlider();
      if (from <= to) {
        this.selectors.toSlider.value = to;
        this.selectors.toPlaceholder.textContent = to;
        this.dataset.max = to;
      } else {
        this.selectors.toPlaceholder.textContent = from;
        this.selectors.toSlider.value = from;
        this.dataset.max = from;
      }
      this.toggleSliderVisibility(from, to);
    }

    /**
     * Toggles the visibility of sliders when they overlap at min or max values.
     * @param {number} from - The value of the "from" slider.
     * @param {number} to - The value of the "to" slider.
     * @returns {void}
     */
    toggleSliderVisibility(from, to) {
      const { fromSlider, toSlider } = this.selectors;
      if (Number(toSlider.max) === from) toSlider.classList.add(this.classes.pointerEventsNone);
      else toSlider.classList.remove(this.classes.pointerEventsNone);
      if (Number(fromSlider.min) === to) fromSlider.classList.add(this.classes.pointerEventsNone);
      else fromSlider.classList.remove(this.classes.pointerEventsNone);
    }

    /**
     * Parses the values of the given inputs or sliders.
     * @param {HTMLElement} currentFrom - The "from" element.
     * @param {HTMLElement} currentTo - The "to" element.
     * @returns {Array<number>} The parsed values.
     */
    getParsed(currentFrom, currentTo) {
      const from = parseInt(currentFrom.value, 10);
      const to = parseInt(currentTo.value, 10);
      return [from, to];
    }

    /**
     * Fills the slider with the appropriate colors based on the range.
     * @returns {void}
     */
    fillSlider() {
      const { fromSlider, toSlider } = this.selectors;
      const rangeDistance = toSlider.max - toSlider.min;
      const fromPosition = fromSlider.value - toSlider.min;
      const toPosition = toSlider.value - toSlider.min;

      toSlider.style.background = `linear-gradient(
        to right,
        #C6C6C6 0%,
        #C6C6C6 ${(fromPosition / rangeDistance) * 100}%,
        #000000 ${(fromPosition / rangeDistance) * 100}%,
        #000000 ${(toPosition / rangeDistance) * 100}%, 
        #C6C6C6 ${(toPosition / rangeDistance) * 100}%, 
        #C6C6C6 100%)`;
    }
  }

  window.customElements.define('wls-double-range-input', WLSDoubleRangeInput);
}
